import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InboxContactReadV2 } from "@hilos/types/conversation";
import {
  ContactTag,
  InboxSettings,
  UserSimple,
} from "@hilos/types/private-schema";
import useDateFnsFormatIntlLocale from "src/hooks/useDateFnsFormatLocale";
import { classNames, contactToString } from "../../Helpers";
import { getDateTimeFromTimestamp } from "../../helpers/date";
import AssigneeList from "./AssigneeList";
import ContactNameWithStatus from "./ContactNameWithStatus";
import ContactTagList from "./Conversation/ContactTagList";
import ConversationStatusBadge from "./Conversation/ConversationStatusBadge";
import ConversationTagList from "./Conversation/ConversationTagList";
import WhatsAppMessageOneLinePreview from "./Conversation/WhatsAppMessageOneLinePreview";

interface ConversationItemProps {
  inboxContact: InboxContactReadV2;
  isSelected: boolean;
  isSelectedAll: boolean;
  showSelection: boolean;
  isLargeScreen?: boolean;
  currentInboxContactId?: string;
  inboxSettings: InboxSettings;
  onSelectContact: (id: string) => void;
}

function ConversationItem({
  inboxContact,
  isSelected,
  isSelectedAll,
  showSelection,
  isLargeScreen = false,
  currentInboxContactId,
  inboxSettings,
  onSelectContact,
}: ConversationItemProps) {
  const { t } = useTranslation();
  const { formatWithLocale } = useDateFnsFormatIntlLocale();
  const isTouching = useRef(false);
  const location = useLocation();

  const [phone, fullName, contactTags] = useMemo(() => {
    if (inboxContact.contact) {
      return [
        inboxContact.contact.phone,
        contactToString(inboxContact.contact),
        inboxContact.contact.tags as unknown as { tag: ContactTag }[],
      ];
    }

    return [];
  }, [inboxContact]);

  const [conversationStatus, conversationAssigned, conversationTags] =
    useMemo(() => {
      if (inboxContact.last_conversation) {
        return [
          inboxContact.last_conversation.status,
          inboxContact.last_conversation.assigned as {
            user: UserSimple;
          }[],
          inboxContact.last_conversation.tags,
        ];
      }

      return [];
    }, [inboxContact]);

  const [
    hasLastMessage,
    lastMessageStatus,
    lastMessageBody,
    lastMessageType,
    lastMessageDeleted,
  ] = useMemo(() => {
    if (inboxContact.last_message) {
      return [
        Boolean(inboxContact.last_message),
        inboxContact.last_message.status,
        inboxContact.last_message.body,
        inboxContact.last_message.msg_type,
        inboxContact.last_message.is_deleted,
      ];
    }

    return [];
  }, [inboxContact]);

  const backgroundColor = useMemo(
    () =>
      inboxContact.id === currentInboxContactId ? "bg-gray-200" : "bg-white",
    [inboxContact.id, currentInboxContactId]
  );

  const handleTouchStart = useCallback(() => (isTouching.current = true), []);

  const handleTouchEnd = useCallback(() => (isTouching.current = false), []);

  const handleContextMenu = useCallback(
    (event) => {
      if (isTouching.current) {
        onSelectContact(inboxContact.id);
        event.preventDefault();
      }
    },
    [inboxContact.id, onSelectContact]
  );

  const handleChangeCheckbox = useCallback(() => {
    try {
      if (!("ontouchstart" in document.documentElement)) {
        onSelectContact(inboxContact.id);
      }
    } catch {}
  }, [inboxContact, onSelectContact]);

  const handleSelectContact = useCallback(
    (event) => {
      if (event.shiftKey) {
        onSelectContact(inboxContact.id);
        event.preventDefault();
      }
    },
    [inboxContact.id, onSelectContact]
  );

  const hasUnreadMesages =
    inboxContact.is_unread || Number(inboxContact.unseen_messages) > 0;

  const showBottomBar =
    (inboxSettings.show_assignees ||
      inboxSettings.show_conversation_tags ||
      inboxSettings.show_contact_tags) &&
    ((contactTags ?? []).length > 0 ||
      (conversationTags ?? []).length > 0 ||
      (conversationAssigned ?? []).length > 0);

  return (
    <li
      data-cursor={inboxContact.id}
      data-tour={phone}
      className={classNames(
        "flex flex-row w-full group select-none overflow-hidden",
        backgroundColor
      )}
    >
      <label
        className={classNames(
          "flex shrink-0 h-auto w-12 transition-spacing justify-center items-center appearance-none",
          "outline-none overflow-hidden group-hover:bg-gray-300",
          !isSelectedAll && "cursor-pointer",
          !showSelection && "-ml-8 hover:ml-0"
        )}
        onChange={handleChangeCheckbox}
      >
        <input
          readOnly
          type="checkbox"
          checked={isSelected || isSelectedAll}
          className={classNames(
            "h-4 w-4 rounded border-gray-300 focus:ring-0 focus:ring-offset-0",
            isSelectedAll ? "text-indigo-400" : "text-indigo-600"
          )}
        />
      </label>
      <Link
        to={`/inbox/conversation/${inboxContact.id}${
          isLargeScreen ? "/details" : ""
        }${location.search}`}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
        onContextMenu={handleContextMenu}
        onClick={handleSelectContact}
        className="flex flex-col py-3 pr-4 gap-1 group-hover:bg-gray-300 shrink w-full select-none overflow-hidden"
      >
        <div className="flex w-full gap-2 items-center justify-between overflow-scroll no-scrollbar">
          <div className="flex items-center justify-center max-w-[calc(100%-3rem)] truncate">
            {inboxContact && (
              <>
                {inboxContact.channel ? (
                  <ContactNameWithStatus
                    name={fullName}
                    channel={inboxContact.channel}
                    lastInboundMessageOn={inboxContact.last_inbound_message_on}
                    className={classNames(
                      "font-medium text-gray-700",
                      hasUnreadMesages && "font-semibold text-gray-800"
                    )}
                  />
                ) : (
                  <ContactNameWithStatus
                    name={fullName}
                    channel={inboxContact.channel_id}
                    lastInboundMessageOn={inboxContact.last_inbound_message_on}
                    className={classNames(
                      "font-medium text-gray-700",
                      hasUnreadMesages && "font-semibold text-gray-800"
                    )}
                  />
                )}
              </>
            )}
          </div>

          <div className="flex flex-row gap-1">
            {inboxSettings.show_conversation_status && (
              <ConversationStatusBadge status={conversationStatus} />
            )}

            {hasUnreadMesages && (
              <div className="flex h-5 w-5 aspect-square items-center justify-center rounded-full bg-indigo-500 px-1">
                <span className="text-xs font-medium text-indigo-100">
                  {inboxContact.unseen_messages || ""}
                </span>
              </div>
            )}
          </div>
        </div>{" "}
        <div className="flex-col no-scrollbar"></div>
        <div className="flex flex-row items-center justify-between overflow-hidden space-x-2">
          {hasLastMessage ? (
            <span
              className={classNames(
                "truncate text-xs text-gray-600",
                hasUnreadMesages && "font-semibold text-gray-800"
              )}
            >
              {lastMessageStatus === "failed" && (
                <FontAwesomeIcon
                  // @ts-ignore
                  icon={faExclamationCircle}
                  className="text-red-500"
                />
              )}{" "}
              <WhatsAppMessageOneLinePreview
                message={inboxContact.last_message}
                body={lastMessageBody}
                type={lastMessageType}
                deleted={lastMessageDeleted}
              />
            </span>
          ) : (
            <span className="text-xs text-gray-600">
              {t("inbox:no-messages", "No messages yet...")}
            </span>
          )}
          {inboxContact.last_message_on && (
            <span
              className={classNames(
                "text-xs text-gray-400",
                hasUnreadMesages && "font-semibold text-gray-800"
              )}
            >
              {getDateTimeFromTimestamp(
                inboxContact.last_message_on,
                formatWithLocale
              )}
            </span>
          )}
        </div>
        {showBottomBar && (
          <div className="flex flex-row pt-1 items-center justify-between gap-2">
            {inboxSettings.show_assignees && (
              <AssigneeList assignees={conversationAssigned} />
            )}
            <div className="flex flex-row items-end justify-end w-full overflow-hidden gap-1">
              {inboxSettings.show_conversation_tags && (
                <ConversationTagList tags={conversationTags} />
              )}
              {inboxSettings.show_contact_tags && (
                <ContactTagList tags={contactTags} />
              )}
            </div>
          </div>
        )}
      </Link>
    </li>
  );
}

export default ConversationItem;
